import { useCallback, useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';

// Mirrors the cloud device_state row (state/capabilities are arbitrary JSON the
// booth reports; keys inside are passed through as the device sent them).
export interface DeviceState {
    id: number;
    deviceKeyId: string;
    orgId: number;
    state: Record<string, any> | null;
    capabilities: Record<string, any> | null;
    snapshotUrl: string | null;
    reportedAt: string;
    updatedAt: string;
}

export interface RemoteSession {
    id: number;
    deviceKeyId: string;
    startedBy: number | null;
    startedByUsername: string | null;
    startedByEmail: string | null;
    startedByFirstName: string | null;
    startedByLastName: string | null;
    startedAt: string;
    lastKeepaliveAt: string;
    expiresAt: string;
}

export type CommandStatus =
    | 'pending'
    | 'sent'
    | 'acked'
    | 'failed'
    | 'expired';

export interface DeviceCommand {
    id: number;
    deviceKeyId: string;
    commandType: string;
    payload: Record<string, any> | null;
    status: CommandStatus;
    result: Record<string, any> | null;
    error: string | null;
    createdBy: number | null;
    createdByUsername?: string | null;
    createdAt: string;
    sentAt: string | null;
    ackedAt: string | null;
    expiresAt: string;
}

export interface DevicePhoto {
    id: number;
    shareIdentifier: string;
    activationId: number;
    thumbnailUri: string | null;
    thumbnail800pxUri: string | null;
    thumbnailTinyUri: string | null;
    originalUri: string | null;
    capturedAt: number | null;
    createdAt: string;
}

const ACTIVE_POLL_MS = 2500;
const KEEPALIVE_MS = 15000;
const PHOTOS_POLL_MS = 10000;

export default function useRemoteControl(orgId: number, deviceKeyId: string) {
    const api = useAuthenticatedApi();

    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [state, setState] = useState<DeviceState | null>(null);
    const [sessions, setSessions] = useState<RemoteSession[]>([]);
    const [history, setHistory] = useState<DeviceCommand[]>([]);
    const [photos, setPhotos] = useState<DevicePhoto[]>([]);
    const [error, setError] = useState<string | null>(null);

    const mySessionId = useRef<number | null>(null);
    const base = `/${orgId}/devices/${deviceKeyId}`;

    const fetchState = useCallback(async () => {
        try {
            const response = await api.get(`${base}/state`);
            setState(response.data);
        } catch (err) {
            // 404 simply means the device has not reported state yet.
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const fetchSessions = useCallback(async () => {
        try {
            const response = await api.get(`${base}/sessions`);
            setSessions(response.data);
        } catch (err) {
            console.error('Error fetching sessions', err);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const fetchHistory = useCallback(async () => {
        try {
            const response = await api.get(`${base}/commands/history`);
            setHistory(response.data);
        } catch (err) {
            console.error('Error fetching history', err);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const fetchPhotos = useCallback(async () => {
        try {
            const response = await api.get(`${base}/photos`);
            setPhotos(response.data);
        } catch (err) {
            console.error('Error fetching photos', err);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const connect = useCallback(async () => {
        setConnecting(true);
        setError(null);
        try {
            const response = await api.post(`${base}/sessions`, {});
            mySessionId.current = response.data?.session?.id ?? null;
            if (response.data?.state) {
                setState(response.data.state);
            }
            setConnected(true);
        } catch (err) {
            console.error('Error connecting', err);
            setError('Failed to start remote control session');
        } finally {
            setConnecting(false);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const disconnect = useCallback(async () => {
        const id = mySessionId.current;
        mySessionId.current = null;
        setConnected(false);
        if (id) {
            try {
                await api.delete(`${base}/sessions/${id}`);
            } catch (err) {
                // Session will lapse via TTL even if this fails.
            }
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [base]);

    const endSession = useCallback(
        async (sessionId: number) => {
            try {
                await api.delete(`${base}/sessions/${sessionId}`);
            } catch (err) {
                console.error('Error ending session', err);
            }
            if (sessionId === mySessionId.current) {
                mySessionId.current = null;
                setConnected(false);
            }
            fetchSessions();
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [base, fetchSessions],
    );

    const sendCommand = useCallback(
        async (commandType: string, payload?: Record<string, any>) => {
            try {
                const response = await api.post(`${base}/commands`, {
                    commandType,
                    payload: payload ?? null,
                });
                fetchHistory();
                return response.data as DeviceCommand;
            } catch (err) {
                console.error('Error sending command', err);
                setError(`Failed to send ${commandType}`);
                return null;
            }
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [base, fetchHistory],
    );

    // While connected: fast poll for state/sessions/history + keepalive.
    useEffect(() => {
        if (!connected) {
            return;
        }

        fetchState();
        fetchSessions();
        fetchHistory();
        fetchPhotos();

        const poll = setInterval(() => {
            fetchState();
            fetchSessions();
            fetchHistory();
        }, ACTIVE_POLL_MS);

        const photosPoll = setInterval(() => {
            fetchPhotos();
        }, PHOTOS_POLL_MS);

        const keepalive = setInterval(() => {
            api.post(`${base}/sessions`, {}).catch(() => {});
        }, KEEPALIVE_MS);

        return () => {
            clearInterval(poll);
            clearInterval(photosPoll);
            clearInterval(keepalive);
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [connected]);

    // Best-effort end of our own session when leaving the page.
    useEffect(() => {
        return () => {
            const id = mySessionId.current;
            if (id) {
                api.delete(`${base}/sessions/${id}`).catch(() => {});
            }
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return {
        connected,
        connecting,
        state,
        sessions,
        history,
        photos,
        error,
        connect,
        disconnect,
        endSession,
        sendCommand,
        refreshState: fetchState,
        refreshHistory: fetchHistory,
        refreshPhotos: fetchPhotos,
    };
}
